import type { StaffNavNode } from './nav-node'

/** Group ids in the tree, excluding Account (pinned and collapsed by default). */
export function workGroupIds(tree: StaffNavNode[]): Set<string> {
  const ids = new Set<string>()
  function walk(nodes: StaffNavNode[]) {
    for (const node of nodes) {
      if (node.kind !== 'group') continue
      if (node.id !== 'account') ids.add(node.id)
      walk(node.children)
    }
  }
  walk(tree)
  return ids
}

export function filterNavTree(tree: StaffNavNode[], query: string): StaffNavNode[] {
  const q = query.trim().toLowerCase()
  if (!q) return tree

  function filterNode(node: StaffNavNode): StaffNavNode | null {
    if (node.kind === 'leaf') {
      const hay = [node.label, ...node.keywords].join(' ').toLowerCase()
      return hay.includes(q) ? node : null
    }
    const groupHay = [node.label, ...node.keywords].join(' ').toLowerCase()
    const children = node.children
      .map(filterNode)
      .filter((n): n is StaffNavNode => n != null)
    if (groupHay.includes(q) || children.length > 0) {
      return { ...node, children }
    }
    return null
  }

  return tree
    .map(filterNode)
    .filter((n): n is StaffNavNode => n != null)
}

export function expandedGroupIdsForPath(tree: StaffNavNode[], pathname: string): Set<string> {
  const expanded = new Set<string>()

  function walk(nodes: StaffNavNode[], ancestors: string[]): boolean {
    for (const node of nodes) {
      if (node.kind === 'leaf') {
        if (pathname === node.path || pathname.startsWith(`${node.path}/`)) {
          for (const id of ancestors) expanded.add(id)
          return true
        }
      } else if (walk(node.children, [...ancestors, node.id])) {
        return true
      }
    }
    return false
  }

  walk(tree, [])
  return expanded
}

/** When the tree has exactly one collapsible group (ignoring Account), expand it. */
export function singleGroupIdsToExpand(tree: StaffNavNode[]): Set<string> {
  const groups = tree.filter((n) => n.kind === 'group' && n.id !== 'account')
  if (groups.length === 1 && groups[0]?.kind === 'group') {
    return new Set([groups[0].id])
  }
  return new Set()
}

/**
 * Keep groups the user already opened. First paint (or newly appeared groups after
 * session load) opens every work group so Dashboard and sibling items stay visible.
 */
export function mergeExpandedGroupIds(
  tree: StaffNavNode[],
  pathname: string,
  previous: Set<string> | null,
  knownGroupIds: Set<string>,
): Set<string> {
  const workIds = workGroupIds(tree)
  const next = new Set(previous ?? [])

  if (!previous) {
    for (const id of workIds) next.add(id)
  } else {
    for (const id of workIds) {
      if (!knownGroupIds.has(id)) next.add(id)
    }
  }

  for (const id of expandedGroupIdsForPath(tree, pathname)) next.add(id)
  for (const id of singleGroupIdsToExpand(tree)) next.add(id)
  return next
}
